import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { formatGameTime } from "../../format/formatGameTime";
import type { SoulsSources } from "../../model/Analysis";

type SoulSource = SoulsSources["sources"][number]["source"];

const CAPTION = msg({
  id: "analysis.souls.caption",
  message: "Where your souls came from by {time}",
});
const YOU = msg({ id: "analysis.souls.you", message: "you" });
const TYPICAL = msg({ id: "analysis.souls.typical", message: "typical" });

// Источники душ строками. `Record` по union из контракта: новый источник не соберётся без подписи.
const LABELS: Record<SoulSource, MessageDescriptor> = {
  lane: msg({ id: "analysis.souls.lane", message: "Lane troopers" }),
  heroes: msg({ id: "analysis.souls.heroes", message: "Hero kills" }),
  neutral: msg({ id: "analysis.souls.neutral", message: "Neutral camps" }),
  objectives: msg({ id: "analysis.souls.objectives", message: "Objectives" }),
  treasure: msg({ id: "analysis.souls.treasure", message: "Crates and urns" }),
};

const ORDER: readonly SoulSource[] = ["lane", "heroes", "neutral", "objectives", "treasure"];

// Меньше этой доли от типичного — отставание: число подсвечено. Только акцент для глаза,
// оценок здесь нет.
const BEHIND = 0.8;

type SoulsSourcesBarsProps = {
  sources: SoulsSources;
};

// Откуда души к моменту сравнения: полоска игрока и отметка типичного у сильных игроков
// на герое. Источник, где оба нуля, не показывается.
export const SoulsSourcesBars = ({ sources }: SoulsSourcesBarsProps) => {
  const { _, i18n } = useLingui();
  const rows = ORDER.flatMap((source) =>
    sources.sources.filter((entry) => entry.source === source && entry.souls + entry.typical > 0),
  );
  if (rows.length === 0) {
    return null;
  }
  const max = Math.max(...rows.flatMap((row) => [row.souls, row.typical]), 1);
  const number = new Intl.NumberFormat(i18n.locale);
  const percent = (souls: number): string => `${((souls / max) * 100).toFixed(1)}%`;
  return (
    <figure className="m-0 flex flex-col gap-2.5">
      <figcaption className="text-[13px] text-subtle">
        {_({ ...CAPTION, values: { time: formatGameTime(sources.time_s) } })}
      </figcaption>
      <ul className="flex flex-col gap-2">
        {rows.map((row) => {
          const behind = row.souls < row.typical * BEHIND;
          return (
            <li
              key={row.source}
              data-source={row.source}
              className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)_auto] items-center gap-3 text-sm"
            >
              <span className="text-foreground">{_(LABELS[row.source])}</span>
              <span className="relative block h-2.5 rounded-full bg-surface-raised">
                <span
                  className={cn(
                    "absolute inset-y-0 left-0 rounded-full",
                    behind ? "bg-warn/80" : "bg-accent/80",
                  )}
                  style={{ width: percent(row.souls) }}
                />
                <span
                  aria-hidden="true"
                  className="absolute -top-1 h-[18px] w-0.5 -translate-x-1/2 bg-foreground"
                  style={{ left: percent(row.typical) }}
                />
              </span>
              <span className="text-right font-mono text-[13px] whitespace-nowrap">
                <span className={behind ? "text-warn" : "text-foreground"}>
                  {number.format(row.souls)}
                </span>
                <span className="text-subtle"> / {number.format(row.typical)}</span>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap gap-4 text-xs text-subtle">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2 w-4.5 rounded-full bg-accent/80" />
          {_(YOU)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-3.5 w-0.5 bg-foreground" />
          {_(TYPICAL)}
        </span>
      </div>
    </figure>
  );
};
